import dayjs from "dayjs";

import { resolveMessagingTemplateByCode } from "./resolveMessagingTemplateByCode";
import type { FluxZapBatchTaskView } from "../types";
import type { MessagingTemplate } from "src-next/modules/crm-clinical/messaging/types";

/**
 * Verifica se a Task de lote corresponde ao template configurado na automação birthday.
 * O templateCode da Task pode ser id WhatsApp, sufixo ou nome do modelo.
 */
export function batchTaskMatchesMessageTemplate(
  task: FluxZapBatchTaskView,
  messageTemplateId: string,
  messagingTemplates: MessagingTemplate[],
): boolean {
  const templateId = String(messageTemplateId ?? "").trim();
  if (!templateId) return false;

  const code = String(task.templateCode ?? "").trim();
  if (!code) return false;

  if (code === templateId) return true;

  const resolved = resolveMessagingTemplateByCode(messagingTemplates, code);
  if (resolved?.id === templateId) return true;

  const configured = messagingTemplates.find((t) => t.id === templateId);
  if (!configured) return false;

  const name = configured.name.trim().toLowerCase();
  const label = String(task.templateLabel ?? "").trim().toLowerCase();
  if (name && label && name === label) return true;

  return (
    code.endsWith(`_${templateId}`) ||
    templateId.endsWith(code) ||
    code.toLowerCase() === name
  );
}

/** Retorna true se `authoredOn` cai no mesmo dia civil que `refDate` (fuso local). */
export function isBatchTaskAuthoredOnSameDay(
  authoredOn: string,
  refDate: Date,
): boolean {
  const authored = dayjs(authoredOn);
  if (!authored.isValid()) return false;
  return authored.isSame(dayjs(refDate), "day");
}

/**
 * Localiza o disparo de aniversário de hoje pelo template da automação + data da Task.
 * Se houver múltiplos, retorna o mais recente.
 */
export function resolveBirthdayBatchTaskForToday(params: {
  batchTasks: FluxZapBatchTaskView[];
  messageTemplateId: string | undefined;
  messagingTemplates: MessagingTemplate[];
  refDate?: Date;
}): FluxZapBatchTaskView | null {
  const templateId = String(params.messageTemplateId ?? "").trim();
  if (!templateId || params.batchTasks.length === 0) return null;

  const refDate = params.refDate ?? new Date();

  const matches = params.batchTasks.filter(
    (task) =>
      isBatchTaskAuthoredOnSameDay(task.authoredOn, refDate) &&
      batchTaskMatchesMessageTemplate(
        task,
        templateId,
        params.messagingTemplates,
      ),
  );

  if (matches.length === 0) return null;

  matches.sort(
    (a, b) =>
      new Date(b.authoredOn).getTime() - new Date(a.authoredOn).getTime(),
  );
  return matches[0] ?? null;
}
